// Exclusión de pares NC↔factura con `tipo_relacion_nc = 'anula_error'` (regla 10.221 / 10.222).
//
// Una NC `anula_error` cancela por completo el documento que referencia (fue un error, se reemitió):
// la NC Y la factura anulada deben quedar FUERA de todo cálculo de deuda/saldo/por-pagar, en
// cualquier mes — exactamente como ya lo hace costosER.js del lado del ER. `ajuste_comercial` NO se
// toca (la NC resta normal, el documento referenciado cuenta normal).
//
// Una NC `anula_error` SIN `documento_referenciado_id` (proveedor recurrente que emite varias
// facturas del mismo monto — Entel, Fuenzalida contador; el SII marcó CodRef=1 pero no se pudo
// identificar la factura puntual) NO forma par acá — `facturasAnuladas`/`notasCredito` la ignoran
// (contrato intacto). El ER (costosER.js, inline, NO consume esta función) la deja RESTAR en su
// cuenta_er (regla 10.225). El lado PAGO (Caja) la maneja con `netearNcSinRefPago` (abajo): netea su
// crédito genérico contra las facturas pendientes del proveedor, oldest-first, para que dejen de
// figurar en "por pagar" — igual que ya lo hacía el FIFO de `construirLedgerProveedor`, pero como
// regla explícita del libro (no como dependencia del resultado del FIFO). Por eso esta función
// ADEMÁS expone `notasSinRef` (aditivo; los consumidores viejos que desestructuran solo los dos Sets
// no cambian): las NC `anula_error` sin ref, que antes se descartaban en silencio.
//
// Función PURA: recibe el universo de facturas SII recibidas (incluyendo las NC, que son filas
// `tipo_dte = 61` de la misma tabla). Comparar/guardar por ID (regla 10.7).

import { esNotaCredito } from "./dte";

export const construirExclusionNcAnulaError = (facturas = []) => {
  const facturasAnuladas = new Set(); // ids de facturas anuladas (el `documento_referenciado_id` de la NC)
  const notasCredito = new Set(); // ids de las NC `anula_error` que anulan (CON ref)
  const notasSinRef = []; // NC `anula_error` SIN ref: {id, proveedor_id, monto_total} — para el neteo pago
  for (const f of facturas || []) {
    if (!esNotaCredito(f)) continue;
    if (f?.tipo_relacion_nc !== "anula_error") continue;
    const refId = f?.documento_referenciado_id;
    if (refId == null) {
      // NC sin vincular: no forma par (no toca los Sets), pero se expone para el neteo pago-side.
      notasSinRef.push({ id: Number(f.id), proveedor_id: f.proveedor_id, monto_total: Number(f.monto_total || 0) });
      continue;
    }
    notasCredito.add(Number(f.id));
    facturasAnuladas.add(Number(refId));
  }
  return { facturasAnuladas, notasCredito, notasSinRef };
};

// Neteo pago-side de las NC `anula_error` SIN `documento_referenciado_id` (regla 10.225, lado Caja).
//
// Cada una es una anulación total confirmada cuyo documento puntual el SII no identificó. En el
// ledger FIFO su crédito genérico se aplicaba oldest-first contra las obligaciones del proveedor
// (Pasada 3 de `construirLedgerProveedor`), sacándolas de "por pagar". Cuando el saldo POR FACTURA
// pasa a salir del libro (`resolverSaldo`, R3.2), ese neteo se pierde y la factura anulada reaparece
// como pendiente fantasma. Esta función lo REPRODUCE como regla explícita del libro: por proveedor,
// suma el crédito de sus NC sin-ref y lo aplica oldest-first (por `fecha_emision`, desempate por id)
// contra el saldo pendiente de sus facturas, devolviendo cuánto se absorbió de cada una.
//
// PURA. NO depende del resultado del FIFO: recibe las facturas pendientes del libro (con su
// `saldoPendiente` en la moneda del documento) que arma el consumidor a partir de su propia cola de
// débitos + `resolverSaldo`. Devuelve `Map<facturaId, montoAbsorbido>`; el consumidor resta ese monto
// del saldo (una factura que queda en ~0 sale de "por pagar"). Oldest-first + reducción parcial =
// mismo criterio que el FIFO; para el caso real (crédito == monto de UNA factura) absorbe esa factura
// entera y agota el crédito, sin tocar las demás.
export const netearNcSinRefPago = ({ notasSinRef = [], facturasPendientes = [] } = {}) => {
  const absorbido = new Map(); // facturaId -> monto absorbido (moneda del documento)
  if (notasSinRef.length === 0 || facturasPendientes.length === 0) return absorbido;

  // Crédito genérico total por proveedor (suma de las NC sin-ref de ese proveedor).
  const creditoPorProveedor = new Map();
  for (const nc of notasSinRef) {
    if (nc?.proveedor_id == null || !(Number(nc.monto_total) > 0)) continue;
    creditoPorProveedor.set(nc.proveedor_id, (creditoPorProveedor.get(nc.proveedor_id) || 0) + Number(nc.monto_total));
  }

  // Facturas pendientes agrupadas por proveedor, oldest-first (fecha_emision asc, desempate por id).
  const pendientesPorProveedor = new Map();
  for (const f of facturasPendientes) {
    if (f?.proveedor_id == null || !(Number(f.saldoPendiente) > 0)) continue;
    if (!pendientesPorProveedor.has(f.proveedor_id)) pendientesPorProveedor.set(f.proveedor_id, []);
    pendientesPorProveedor.get(f.proveedor_id).push(f);
  }

  for (const [provId, credInicial] of creditoPorProveedor) {
    let credito = credInicial;
    const cola = [...(pendientesPorProveedor.get(provId) || [])].sort(
      (a, b) => String(a.fecha_emision).localeCompare(String(b.fecha_emision)) || Number(a.id) - Number(b.id)
    );
    for (const f of cola) {
      if (credito <= 0.01) break;
      const aplicar = Math.min(credito, Number(f.saldoPendiente));
      if (aplicar > 0.01) {
        absorbido.set(Number(f.id), (absorbido.get(Number(f.id)) || 0) + aplicar);
        credito -= aplicar;
      }
    }
  }
  return absorbido;
};
